import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, MapPin, ExternalLink } from 'lucide-react';
import CategoryNav from './CategoryNav';
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';

const SENIOR_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "category", label: "Type", placeholder: "e.g. Insurance, Community" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "website", label: "Website URL", placeholder: "https://…" },
  { key: "facebook", label: "Facebook URL", placeholder: "https://facebook.com/…" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

const SENIOR_CATEGORIES = ['Insurance'] as const;

export const SENIOR_ENTRIES = [
  {
    id: 'bhrigu-matru-sewa',
    name: 'Bhrigu Matru Sewa',
    address: '1806 COTTONWOOD ROAD, Plano, TX, United States, 75024',
    website: '',
    facebook: 'https://www.facebook.com/profile.php?id=61563310853751',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783431867413-cqxosf.png',
  },
  {
    id: 'dfw-senior-citizen-samaj',
    name: 'DFW Senior Citizen Samaj',
    address: '3119 Jubilee Trail, Dallas, TX 75229',
    website: 'https://dfwscs.org/',
    facebook: '',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783432343213-sj0an.png',
  },
  {
    id: 'bhartiya-nivas',
    name: 'Bhartiya Nivas',
    address: '7750 Lyndon B Johnson Fwy, Dallas, TX 75251',
    website: 'https://bhartiyanivas.com/',
    facebook: '',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783432462887-csvrcm.png',
  },
];

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?w=600&h=400&fit=crop';

function mapsUrl(entry: { name: string; address?: string }) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${entry.name}, ${entry.address || ''}`)}`;
}

export function SeniorCard({ entry, index, href }: { entry: { name: string; address?: string; website?: string; facebook?: string; image?: string }; index: number; href?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col group"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="relative w-full h-36 overflow-hidden bg-gray-50">
        <img
          src={entry.image || FALLBACK_IMAGE}
          alt={entry.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="flex flex-col flex-1 px-3 py-3 gap-1.5">
        <h3 className="font-semibold text-gray-900 leading-snug text-[14px]">
          {href ? (
            <a href={href} className="hover:text-[#f97316] transition-colors">
              {entry.name}
            </a>
          ) : (
            entry.name
          )}
        </h3>
        {entry.address && (
          <div className="flex items-start gap-1">
            <MapPin className="w-3 h-3 text-orange-500 shrink-0 mt-0.5" />
            <span className="text-[11px] text-gray-500 leading-snug">{entry.address}</span>
          </div>
        )}
        <div className="mt-auto pt-1.5 flex flex-col gap-1.5">
          <SocialLinks item={entry} variant="bar" />
          <a
            href={mapsUrl(entry)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1 font-semibold text-white bg-[#f97316] hover:bg-[#ea580c] rounded-lg transition-colors duration-200 px-3 py-1.5 text-[12px]"
          >
            <ExternalLink className="w-3 h-3" /> View Location
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function SeniorCitizen() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const mc = useManagedCollection<any>("senior_citizen", SENIOR_ENTRIES, (a, i) => slugify(a.name || String(i)));

  const filtered = mc.items.filter((a) => {
    const q = searchQuery.trim().toLowerCase();
    const matchesQuery = !q || a.name.toLowerCase().includes(q) || (a.address || '').toLowerCase().includes(q);
    const matchesCategory = !selectedCategory || (a.category || '') === selectedCategory;
    return matchesQuery && matchesCategory;
  });

  // Respect the admin-managed order (drag-reorder) instead of alphabetical, so
  // editing a card doesn't move it and reordering persists on the public site.
  const sorted = [...filtered].sort((a: any, b: any) => (a._order ?? 0) - (b._order ?? 0));

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative w-full" style={{ minHeight: "160px" }}>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-07/a10eed068e85.png"
            alt="South Asian grandparents smiling together"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/45" />
          <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: 'absolute', left: 0, bottom: '-1px', width: '100%', height: '18px', display: 'block' }}>
            <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="relative max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex items-center justify-center" style={{ zIndex: 10, minHeight: "160px" }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="w-full max-w-[600px]">
            <div className="rounded-md" style={{ boxShadow: '0 0 40px 12px rgba(255,255,255,0.45), 0 0 80px 24px rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <div className="flex bg-white rounded-md shadow-md h-[40px]">
                <div className="flex-1 px-3 flex items-center min-h-[40px]">
                  <span className="text-[11px] text-gray-600 font-semibold shrink-0 mr-2 hidden sm:inline">Find resources</span>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="e.g. Senior Samaj, Nivas"
                    className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent"
                  />
                </div>
                <button className="bg-[#f97316] hover:bg-[#ea580c] text-white text-[13px] font-bold px-5 transition-colors duration-200 shrink-0 min-h-[40px] rounded-r-md">
                  <span className="flex items-center justify-center gap-1.5">
                    <Search className="w-3.5 h-3.5 sm:hidden" />
                    Search
                  </span>
                </button>
              </div>
            </div>

          </motion.div>
        </div>
      </section>
      <NewsTicker />

      {/* Secondary Category Nav */}
      <CategoryNav />

      {/* ── Filter Bar ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1320px] mx-auto px-4 pt-6 pb-4">
          <p className="text-[11px] font-bold text-gray-400 uppercase mb-4" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '0.12em' }}>
            Explore by Type
          </p>
          <div className="flex gap-3 flex-wrap">
            {SENIOR_CATEGORIES.map((cat) => {
              const isActive = selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(isActive ? null : cat)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-[13px] font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-[#f97316] text-white border-[#f97316] shadow-sm'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-[#f97316] hover:text-[#f97316]'
                  }`}
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  {cat}
                </button>
              );
            })}
            {selectedCategory && (
              <button
                onClick={() => setSelectedCategory(null)}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-4 py-2 text-[13px] text-gray-500 hover:text-gray-700 transition-colors duration-150"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Listings */}
      <section className="bg-gray-50/50 py-8">
        <div className="max-w-[1320px] mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <h2 className="text-[18px] sm:text-[20px] md:text-[22px] font-bold text-gray-900 mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Senior Citizen Resources in Dallas-DFW
            </h2>
            <p className="text-[12px] sm:text-[13px] text-gray-500 mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Community organizations and resources for seniors in the Dallas–Fort Worth area.
            </p>

            <AdminAddBar mc={mc} fields={SENIOR_FIELDS} label="Add Senior Listing" />

            {sorted.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {sorted.map((entry, index) => (
                  <AdminItem key={entry._key} mc={mc} item={entry} fields={SENIOR_FIELDS} title="Senior Listing">
                    <SeniorCard entry={entry} index={index} href={`/senior-citizen/${entry._key}`} />
                  </AdminItem>
                ))}
              </div>
            )}

            {sorted.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-400 text-[14px]">No senior citizen listings found for this search.</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
